import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logError } from 'services/logging/logging';
import { useResendMessageToReceiver } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { MessageV2DTO } from 'support/types';

import { ActionConfig } from '../components/types';
import { ResendAsReceiverModal } from './components/ResendAsReceiverModal/ResendAsReceiverModal';
import { resendAs, resolveBackendErrorSubtitle } from './helpers';

/**
 * Deliberately blind to the 422 `code`: each of the seven refusal codes is only reachable by
 * racing a stale `can_resend`, and the generic copy holds true for all of them.
 *
 * i18n keys
 * t('transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorNotResendable')
 * t('transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorForbidden')
 * t('transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorNotFound')
 */
const errorSubtitleKeys = {
  422: 'transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorNotResendable',
  403: 'transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorForbidden',
  404: 'transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorNotFound',
};

export const useResendAsReceiverAction = (message: MessageV2DTO): ActionConfig => {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: resendAsReceiver, isLoading: isResending } = useResendMessageToReceiver();

  const handleResend = (reason: string) => {
    resendAsReceiver(
      { messageId: message.id, reason: reason.trim() || null },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          addNotification(
            { title: t('transactions:show.actionButtons.resendAsReceiverConfirmationModal.successNotification') },
            NotificationType.success,
          );
        },
        onError: (error) => {
          logError(error);
          addNotification(
            {
              title: t('transactions:show.actionButtons.resendAsReceiverConfirmationModal.errorNotification'),
              subtitle: resolveBackendErrorSubtitle(error, t, errorSubtitleKeys),
            },
            NotificationType.error,
          );
        },
      },
    );
  };

  const shouldBeDisabled = (msg: MessageV2DTO) => !msg.can_resend;

  /**
   * i18n keys
   * t('transactions:show.actionButtons.resendAsReceiver.label')
   */
  return {
    label: 'transactions:show.actionButtons.resendAsReceiver.label',
    onClick: () => setIsModalOpen(true),
    analyticsId: 'transactions:resend_as_receiver',
    group: 'resend',
    secondaryOptionOnly: true,
    test: (msg) => resendAs(msg, currentPartner?.id) === 'receiver',
    disabled: shouldBeDisabled,
    description: (msg) =>
      shouldBeDisabled(msg) ? t('transactions:show.actionButtons.resendAsReceiver.description') : undefined,
    renderModal: () => (
      <ResendAsReceiverModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        onConfirm={handleResend}
        isLoading={isResending}
      />
    ),
  };
};
